import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  InputGroup,
  InputLeftElement,
  Input,
  Button,
  Select,
  Flex,
  useColorModeValue,
  Icon,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  Avatar,
  Badge,
  Heading,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchIcon } from '@chakra-ui/icons';
import { useApi } from '@/contexts/ApiContext';
import type { CordeCommonModelsDtoUserprofileUserProfile } from '@/api/generated/v1/api';

// Icons for profile sections
const MyProfilesIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
    />
  </Icon>
);

const SearchProfilesIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
    />
  </Icon>
);


export const SearchProfilesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { UserprofileApi } = useApi();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('display_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [profiles, setProfiles] = useState<CordeCommonModelsDtoUserprofileUserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Theme colors
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Load profiles
  const loadProfiles = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await UserprofileApi.listUserProfiles(
        undefined, // userId - load all profiles
        20, // limit
        0,  // offset
        sortBy,
        sortOrder as any, // cast to enum
        searchQuery.trim() || undefined // search parameter
      );
      
      const data = (response as any)?.data || response;
      setProfiles(data?.profiles || []);
    } catch (err: any) {
      console.error('Error loading profiles:', err);
      const errorMessage = err.message || t('profiles.failedToLoadProfiles');
      setError(errorMessage);
      toast({
        title: t('common.error'),
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load and sidebar setup
  useEffect(() => {
    // Load initial profiles - but don't await here to avoid breaking useEffect
    loadProfiles().catch(console.error);

    // Update right sidebar with actions
    if (window.updateRightSidebar) {
      window.updateRightSidebar([
        {
          label: t('profiles.createProfile'),
          colorScheme: 'green',
          onClick: () => {
            navigate('/profile/create');
          },
        },
        {
          label: t('profiles.myProfiles'),
          colorScheme: 'blue',
          variant: 'outline',
          onClick: () => {
            navigate('/profiles/my-profiles');
          },
        },
      ]);
    }

    // Update left sidebar with subsections info for profiles
    if (window.updateLeftSidebar) {
      const profileSections = [
        {
          path: '/profiles/my-profiles',
          label: t('profiles.myProfiles'),
          icon: MyProfilesIcon,
        },
        {
          path: '/profiles/search',
          label: t('profiles.searchProfiles'),
          icon: SearchProfilesIcon,
        },
      ];

      window.updateLeftSidebar({
        section: 'profiles',
        subsections: profileSections
      });
    }

    return () => {
      if (window.updateRightSidebar) {
        window.updateRightSidebar([]);
      }
    };
  }, [t, navigate]);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadProfiles();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, sortBy, sortOrder]);

  const handleSearch = () => {
    loadProfiles();
  };

  const handleViewProfile = (profileId: string) => {
    navigate(`/profiles/${profileId}`);
  };

  const handleSortChange = (value: string) => {
    const [newSortBy, newSortOrder] = value.split('-') as [string, 'asc' | 'desc'];
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };

  // Loading state
  if (isLoading && profiles.length === 0) {
    return (
      <Box minH="100vh" bg={bgColor}>
        <Container maxW="7xl" py={6}>
          <VStack spacing={4} py={8}>
            <Spinner size="lg" />
            <Text>{t('profiles.loadingProfiles')}</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  // Error state
  if (error && profiles.length === 0) {
    return (
      <Box minH="100vh" bg={bgColor}>
        <Container maxW="7xl" py={6}>
          <Box textAlign="center" py={8}>
            <Alert status="error" mb={4}>
              <AlertIcon />
              {error}
            </Alert>
            <Button onClick={loadProfiles} colorScheme="primary">
              {t('common.retry')}
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      <Container maxW="7xl" py={6}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Box>
            <Heading size="lg" mb={2} color="text.primary">
              {t('profiles.searchProfiles', 'Search Profiles')}
            </Heading>
            <Text color="text.secondary" fontSize="lg">
              {t('profiles.searchDescription', 'Find and connect with other users')}
            </Text>
          </Box>

          {/* Search and Filters */}
          <Card bg={cardBg} border="1px" borderColor={borderColor}>
            <CardBody>
              <VStack spacing={4}>
                {/* Search Bar */}
                <InputGroup size="lg">
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.300" />
                  </InputLeftElement>
                  <Input
                    placeholder={t('profiles.searchPlaceholder', 'Search by name, username, or bio...')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </InputGroup>

                {/* Filters */}
                <Flex 
                  direction={{ base: 'column', md: 'row' }} 
                  gap={4} 
                  w="full"
                  justify="space-between"
                  align={{ base: 'stretch', md: 'center' }}
                >
                  <HStack spacing={4} flex={1}>
                    <Select
                      value={`${sortBy}-${sortOrder}`}
                      onChange={(e) => handleSortChange(e.target.value)}
                      maxW="200px"
                    >
                      <option value="display_name-asc">{t('profiles.sortByName', 'Name A-Z')}</option>
                      <option value="display_name-desc">{t('profiles.sortByName', 'Name Z-A')}</option>
                      <option value="created_at-desc">{t('profiles.sortByCreated', 'Newest First')}</option>
                      <option value="created_at-asc">{t('profiles.sortByCreated', 'Oldest First')}</option>
                      <option value="updated_at-desc">{t('profiles.sortByUpdated', 'Recently Updated')}</option>
                    </Select>
                  </HStack>

                  <Button colorScheme="blue" onClick={handleSearch} minW="120px" isLoading={isLoading}>
                    {t('common.search', 'Search')}
                  </Button>
                </Flex>
              </VStack>
            </CardBody>
          </Card>

          {/* Results summary */}
          <HStack justify="space-between" align="center">
            <Text fontSize="sm" color="gray.500">
              {t('profiles.totalProfiles')}: {profiles.length}
            </Text>
            {isLoading && <Spinner size="sm" />}
          </HStack>

          {/* Results */}
          {profiles.length === 0 && !isLoading ? (
            <Box textAlign="center" py={12}>
              <SearchIcon boxSize={12} color="gray.400" />
              <Text fontSize="lg" fontWeight="medium" mt={4} mb={2}>
                {t('profiles.noResults', 'No profiles found')}
              </Text>
              <Text color="text.secondary" textAlign="center">
                {t('profiles.noResultsDescription', 'Try adjusting your search terms or filters')}
              </Text>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {profiles.map((profile) => (
                <Card 
                  key={profile.id} 
                  bg={cardBg} 
                  border="1px" 
                  borderColor={borderColor}
                  _hover={{ 
                    borderColor: 'primary.400',
                    shadow: 'md',
                    transform: 'translateY(-2px)'
                  }}
                  transition="all 0.2s"
                  cursor="pointer"
                  onClick={() => handleViewProfile(profile.id)}
                >
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      {/* Profile Header */}
                      <HStack spacing={3}>
                        <Avatar 
                          size="md" 
                          name={profile.display_name || profile.username} 
                          src={profile.avatar_url}
                          bg="primary.500"
                          color="white"
                        />
                        <VStack align="start" spacing={0} flex={1}>
                          <Text fontWeight="semibold" fontSize="lg">
                            {profile.display_name || profile.username}
                          </Text>
                          <Text color="text.secondary" fontSize="sm">
                            @{profile.username}
                          </Text>
                        </VStack>
                        {profile.profile_type && (
                          <Badge colorScheme="blue" variant="subtle">
                            {profile.profile_type}
                          </Badge>
                        )}
                      </HStack>

                      {/* Profile Details */}
                      <VStack align="start" spacing={2}>
                        {profile.bio && (
                          <Text fontSize="sm" color="text.secondary" noOfLines={2}>
                            {profile.bio}
                          </Text>
                        )}
                        <HStack justify="space-between" fontSize="xs" color="text.secondary" w="full">
                          <Text>
                            {t('profiles.created')}: {profile.created_at ? new Date(profile.created_at).toLocaleDateString() : '-'}
                          </Text>
                          {profile.location && (
                            <Text>
                              📍 {profile.location}
                            </Text>
                          )}
                        </HStack>
                      </VStack>

                      {/* Actions */}
                      <HStack spacing={2}>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewProfile(profile.id);
                          }}
                          flex={1}
                        >
                          {t('profiles.viewProfile', 'View Profile')}
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="green"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Handle connect logic here
                          }}
                          flex={1}
                        >
                          {t('profiles.connect', 'Connect')}
                        </Button>
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </VStack>
      </Container>
    </Box>
  );
};